/**
 * @file StockLevelsTable.tsx
 * @description Остатки таблицей от 1024 — плоская таблица партий
 * (phase19.24 этап 1, шаг 4; SC59 — phase19.31).
 *
 * Построена на `ui/table` темы: шапка 44, строка 52 в одну линию, линии и
 * наведение — из темы; выбранная строка — `data-state="selected"` (подложка
 * и метка слева). Колонки — реестром `stockTableColumns`; галочка отбора —
 * первым столбцом, когда включён отбор «Без места» или по этикеткам; «⋯» —
 * последним, компактная кнопка (32px).
 *
 * Если таблица всё же шире блока (много складов + галочки на 1024), она
 * прокручивается внутри рамки, а не страница.
 */

import { stockTableColumns } from "./stock-level-columns";
import { StockSelectCell } from "./StockLevelCells";
import type { StockRowViewProps } from "./stock-level-format";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { WIDE_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";

export function StockLevelsTable(props: StockRowViewProps) {
  const { items, selectable, isSelected, onToggle, actions, selectState } = props;
  const wide = useMediaQuery(WIDE_QUERY);
  const columns = stockTableColumns({ ...props, wide });

  return (
    <div className={LIST_FRAME_CLASS}>
      <Table aria-label="Остатки по партиям" className={cn("table-fixed", wide ? "min-w-[860px]" : "min-w-[640px]")}>
        <colgroup>
          {selectable ? <col className="w-14" /> : null}
          {columns.map((col) => (
            <col key={col.key} className={col.width} />
          ))}
          <col className="w-14" />
        </colgroup>
        <TableHeader>
          <TableRow>
            {selectable ? (
              <TableHead>
                <span className="sr-only">Отметка</span>
              </TableHead>
            ) : null}
            {columns.map((col) => (
              <TableHead key={col.key} className={cn("whitespace-nowrap", col.align === "end" && "text-right")}>
                {col.header}
              </TableHead>
            ))}
            <TableHead>
              <span className="sr-only">Действия</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((level) => {
            const selected = selectable && isSelected(level);
            return (
              <TableRow key={level.id} data-state={selected ? "selected" : undefined}>
                {selectable ? (
                  <TableCell className="py-1">
                    <StockSelectCell level={level} checked={isSelected(level)} onToggle={onToggle} state={selectState?.(level)} />
                  </TableCell>
                ) : null}
                {columns.map((col) => (
                  <TableCell key={col.key} className={cn("min-w-0 py-1.5", col.align === "end" && "text-right")}>
                    {col.cell(level)}
                  </TableCell>
                ))}
                <TableCell className="py-1">
                  <div className="flex justify-end">{actions(level)}</div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
